import { Trans, useLingui } from '@lingui/react/macro';

import { CardItem } from '@/components/Card/Card';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconNoConnection from '@/icons/NoConnection.svg';
import IconStoreRepair from '@/icons/StoreRepair.svg';
import IconTools from '@/icons/Tools.svg';
import { getGroupedActions, useGetActionName } from '@/models/actionType';
import { formatCityState } from '@/models/address';
import { ArticleWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

export const ArticleServiceChoiceCell = ({ article }: { article: ArticleWithRelations }) => {
  const { t } = useLingui();
  const { articles } = useRequestContext();
  const displayService = (articles[0].view.services.choice.serviceOptions ?? []).length > 1;

  const getActionName = useGetActionName();
  const actions = getGroupedActions(article.snapshot.articleActions);

  const workshopCityState = article.workshop?.address
    ? formatCityState(article.workshop.address)
    : undefined;

  if (
    !(displayService && article.snapshot.serviceChoice) &&
    !(
      article.snapshot.serviceChoice === 'care-repair' &&
      (actions.length > 0 || !!article.workshop)
    )
  ) {
    return <InlineText color="secondary">-</InlineText>;
  }

  return (
    <Stack>
      {displayService && article.snapshot.serviceChoice && (
        <InlineText>{SERVICE_CHOICE_LABEL[article.snapshot.serviceChoice]}</InlineText>
      )}
      {article.snapshot.serviceChoice === 'care-repair' && actions.length > 0 && (
        <ul>
          {actions.map((action) => (
            <li key={action.id}>
              <Paragraph>
                {getActionName({ action })}
                {action.quantity > 1 && <> (x{action.quantity})</>}
              </Paragraph>
            </li>
          ))}
        </ul>
      )}
      {article.snapshot.serviceChoice === 'care-repair' && !!article.workshop && (
        <Stack row gap="1rem" alignItems="center" flexWrap="nowrap">
          <InlineText
            size="200"
            color="secondary"
            ellipsis
            title={`${article.workshop.name}${workshopCityState ? ` • ${workshopCityState}` : ''}`}
          >
            {!!article.workshop.workshopConfig?.nonDigitalized && (
              <Tooltip
                content={t({
                  id: 'article-workshop-cell.non-digitalized.tooltip',
                  message: 'Non-digitalized',
                })}
                hasOverlayArrow
              >
                <Button variant="style-less">
                  <Badge size="x-small" color="secondary" borderRadius="soft" iconOnly>
                    <IconNoConnection />
                  </Badge>
                </Button>
              </Tooltip>
            )}
            {!!article.workshop.name && ` ${article.workshop.name}`}
            {!!workshopCityState && ` • ${workshopCityState}`}
            {article.workshopOrganization?.external && (
              <>
                {' • '}
                <Trans id="workshop.external">External</Trans>
              </>
            )}
          </InlineText>
        </Stack>
      )}
    </Stack>
  );
};

export const ArticleServiceChoiceCardItems = ({ article }: { article: ArticleWithRelations }) => {
  const { articles } = useRequestContext();
  const displayService = (articles[0].view.services.choice.serviceOptions ?? []).length > 1;

  const getActionName = useGetActionName();
  const actions = getGroupedActions(article.snapshot.articleActions);

  return (
    <>
      {displayService &&
        !!article.snapshot.serviceChoice &&
        article.snapshot.serviceChoice !== 'care-repair' && (
          <CardItem key={article.snapshot.serviceChoice}>
            <IconTools style={{ fontSize: '1rem' }} />
            {SERVICE_CHOICE_LABEL[article.snapshot.serviceChoice]}
          </CardItem>
        )}
      {article.snapshot.serviceChoice === 'care-repair' &&
        actions.length > 0 &&
        actions.map((action) => (
          <CardItem key={action.id}>
            <IconTools style={{ fontSize: '1rem' }} />
            {getActionName({ action })}
            {action.quantity > 1 && <> (x{action.quantity})</>}
          </CardItem>
        ))}
      {article.snapshot.serviceChoice === 'care-repair' && !!article.workshop && (
        <CardItem>
          <IconStoreRepair style={{ fontSize: '1rem' }} />
          <span>{article.workshop.name}</span>
        </CardItem>
      )}
    </>
  );
};

const SERVICE_CHOICE_LABEL = {
  'care-repair': (
    <Trans id="request.articles.table.column.service-type.care-and-repair">Care and repair</Trans>
  ),
  exchange: <Trans id="request.articles.table.column.service-type.exchange">Exchange</Trans>,
  'exchange-archival': (
    <Trans id="request.articles.table.column.service-type.exchange-archival">Exchange</Trans>
  ),
  refund: <Trans id="request.articles.table.column.service-type.refund">Refund</Trans>,
  none: <Trans id="request.articles.table.column.service-type.none">None</Trans>,
};
